/**
 * The client asset loader (WR.3.2): resolve an `AssetEntry` by id, load its glTF
 * artifact behind a clean interface, cache it in memory, and **degrade
 * gracefully** to the placeholder primitives (Game.ts) whenever no pack is bound
 * or an entry fails to load — never a hard failure, never a blank world
 * (wr3-asset-pipeline-standup.md §8 graceful-degradation rule).
 *
 * The actual Three.js GLTFLoader/DRACOLoader/KTX2Loader wiring lives in
 * threeLoader.ts and is INJECTED as a `GltfLoadFn`, so this module — and its
 * unit tests — stay free of any GPU/DOM dependency and run under `node --test`
 * (§12 risk 1). The `three` imports here are type-only PLUS one narrow,
 * node-safe runtime exception: `SkeletonUtils.clone` (pure object-graph code,
 * no GPU/DOM), required for correct skinned-template instantiation — see
 * `cloneTemplate` below (O-011, 2026-07-16).
 *
 * Import boundary (AD-122): shared types come from `@great-cloud/shared/client`.
 */

import type { AnimationClip, Object3D } from 'three';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';

import type { AssetEntry } from '@great-cloud/shared/client';

import { AssetManifest, deliveryUrl } from './manifest.js';
import { lodHandle } from './lod.js';
import { decorateLoadedAsset } from './pbr.js';
import { markShared, releaseShared } from './gpuOwnership.js';
import {
  applyEmitterHides,
  attachEmitters,
  emitterBand,
  emitterFactory,
  stripEmitters,
  type EmitterDecoration,
} from './emitters.js';

/**
 * The minimal slice of a parsed glTF the loader needs: its root scene object,
 * plus any animation clips the artifact carries (AP5.S1). Clips were previously
 * dropped here, which is why the rig rendered but never animated — the data was
 * parsed by GLTFLoader and then discarded at this boundary.
 */
export interface GltfResult {
  scene: Object3D;
  /** Clips packed in this artifact; absent/empty for every static world asset. */
  animations?: readonly AnimationClip[];
}

/** Fetches + parses one glTF artifact by URL (injected; see `createThreeGltfLoader`). */
export type GltfLoadFn = (url: string) => Promise<GltfResult>;

export interface AssetLoaderOptions {
  /** The glTF load function (real: `createThreeGltfLoader()`; tests: a stub). */
  load: GltfLoadFn;
  /** Static asset HTTP base for delivery URLs (`assetHttpBase()`); `''` = same-origin. */
  httpBase?: string;
  /** Non-fatal load-failure sink (default `console.warn`); a failure still degrades to a primitive. */
  onWarn?: (message: string, error?: unknown) => void;
}

function defaultWarn(message: string, error?: unknown): void {
  console.warn(`[assets] ${message}`, error ?? '');
}

/** Duck-typed: does this template contain a `SkinnedMesh` anywhere in its graph? */
function containsSkinnedMesh(object: Object3D): boolean {
  // Plain-object test stubs carry no traverse(); they are never skinned.
  if (typeof object.traverse !== 'function') return false;
  let skinned = false;
  object.traverse((node) => {
    if ((node as unknown as { isSkinnedMesh?: unknown }).isSkinnedMesh === true) skinned = true;
  });
  return skinned;
}

/**
 * Clone a cached template for placement (O-011 fix, 2026-07-16 — the live
 * invisible-player defect). `Object3D.clone(true)` is WRONG for a template
 * containing a `SkinnedMesh`: the clone's skeleton keeps referencing the
 * TEMPLATE's bone instances, which sit detached in this cache and are never
 * matrix-updated — the GPU then skins every vertex against stale bone matrices
 * and the avatar renders collapsed/exploded (silently: no error, no warning).
 * `player-rig-v1` (AD-181, wired live by E2.4.S2) was the first skinned asset
 * through this path. Skinned templates therefore clone via
 * `SkeletonUtils.clone` (rebinds each cloned SkinnedMesh to its OWN cloned
 * bones); static templates keep the exact previous `clone(true)` — the world
 * kits render byte-identically. Cloned skinned meshes also get
 * `frustumCulled = false`: their bounds are bind-pose-only, so animation (AP5)
 * could otherwise cull a visible character mid-swing.
 */
function cloneTemplate(template: Object3D): Object3D {
  if (!containsSkinnedMesh(template)) return template.clone(true);
  const copy = cloneSkeleton(template);
  copy.traverse((node) => {
    if ((node as unknown as { isSkinnedMesh?: unknown }).isSkinnedMesh === true) {
      node.frustumCulled = false;
    }
  });
  return copy;
}

/**
 * Resolves and loads renderable templates for a bound manifest, caching each by
 * id. `template(id)` / `instantiate(id)` resolve to `null` for every fallback
 * case (unbound manifest, unknown id, an entry with no `url`, or a load error) so
 * the caller renders its placeholder primitive instead.
 */
export class AssetLoader {
  private readonly manifest: AssetManifest;
  private readonly load: GltfLoadFn;
  private readonly httpBase: string;
  private readonly onWarn: (message: string, error?: unknown) => void;
  /** id → in-flight-or-settled load (de-dupes concurrent requests for one id). */
  private readonly cache = new Map<string, Promise<Object3D | null>>();
  /**
   * Cache key → the clips that artifact carried (AP5.S1). Populated by the same
   * load that fills `cache`, so clips cost no extra request. Clips are immutable
   * shared data: three resolves a clip's tracks against the mixer's root by NODE
   * NAME, and `cloneTemplate` preserves names, so one cached clip safely drives
   * every cloned instance of that rig.
   */
  private readonly clipCache = new Map<string, readonly AnimationClip[]>();

  constructor(manifest: AssetManifest, options: AssetLoaderOptions) {
    this.manifest = manifest;
    this.load = options.load;
    this.httpBase = options.httpBase ?? '';
    this.onWarn = options.onWarn ?? defaultWarn;
  }

  /** Whether a pack is bound; `false` ⇒ every load is a deliberate primitive fallback. */
  get bound(): boolean {
    return this.manifest.bound;
  }

  /**
   * The shared, cached template for an id at a LOD index (do NOT mutate it or add
   * it to a scene directly — use `instantiate()` for a placeable clone). `lod`
   * defaults to 0 (near; an entry with no `lods[]` resolves its base `url`).
   * Each LOD is a distinct artifact, so it is cached under its own key. Resolves
   * to `null` for any fallback case (unbound / unknown / no handle / load error).
   */
  template(id: string, lod = 0): Promise<Object3D | null> {
    const key = lod === 0 ? id : `${id}@${lod}`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const pending = this.loadTemplate(id, lod);
    this.cache.set(key, pending);
    return pending;
  }

  /**
   * A fresh clone of the template at a LOD index, ready to position in the scene,
   * or `null` for a fallback. Cloning lets the scene builder place many instances
   * of one kit piece (the WR.3.4 dense-scatter path instances the placeholder and
   * places upgraded clones at each slot). Skinned templates clone skeleton-aware
   * (see `cloneTemplate`).
   */
  async instantiate(id: string, lod = 0): Promise<Object3D | null> {
    const tpl = await this.template(id, lod);
    return tpl ? cloneTemplate(tpl) : null;
  }

  /**
   * Like `template`, but resolves the entry's full-resolution BASE `url` — the
   * textured render — bypassing the `lods[]` chain (whose near entry is the
   * untextured proxy). Used by full desktop quality and capture-mode art review;
   * mobile/explicit-low keeps the proxy LODs. Cached under a distinct
   * `id@base` key. Resolves to `null` for any fallback case.
   */
  templateBase(id: string): Promise<Object3D | null> {
    const key = `${id}@base`;
    const cached = this.cache.get(key);
    if (cached !== undefined) return cached;
    const pending = this.loadTemplateBase(id);
    this.cache.set(key, pending);
    return pending;
  }

  /** A fresh, placeable clone of the base (textured) template, or `null`. */
  async instantiateBase(id: string): Promise<Object3D | null> {
    const tpl = await this.templateBase(id);
    return tpl ? cloneTemplate(tpl) : null;
  }

  /**
   * Instantiate the graduated base when full detail is preferred, falling back
   * to LOD0 if that base is absent or fails to load. Low/degraded quality goes
   * directly to LOD0. Callers therefore retain the established graceful path
   * without having to duplicate a base→LOD retry.
   */
  async instantiatePreferred(id: string, preferBase: boolean): Promise<Object3D | null> {
    if (!preferBase) return this.instantiate(id, 0);
    const base = await this.instantiateBase(id);
    return base ?? this.instantiate(id, 0);
  }

  /**
   * The animation clips for an asset (AP5.S1), or `[]` when it ships none.
   *
   * Resolution order matters for the rig: gameplay instantiates `body-base` at
   * **lod0**, but `player-rig-v1`'s clips are packed ONLY in the full `url`
   * artifact (`body-base.glb`) — its `lod0` carries the same 24-joint skin with
   * zero clips. So a miss at the requested LOD falls back to the base artifact,
   * which is loaded once per session and cached like any other template. Cheap
   * geometry per player, clips fetched once for all of them.
   */
  async animationClips(id: string, lod = 0): Promise<readonly AnimationClip[]> {
    await this.template(id, lod);
    const lodKey = lod === 0 ? id : `${id}@${lod}`;
    const atLod = this.clipCache.get(lodKey);
    if (atLod !== undefined && atLod.length > 0) return atLod;
    const entry: AssetEntry | undefined = this.manifest.resolve(id);
    if (entry === undefined) return atLod ?? [];
    // When this LOD already RESOLVES to the base artifact (an entry with no
    // `lods[]`), the base fetch would re-download the identical file under a
    // second cache key — so the clips we just cached are all there are.
    if (lodHandle(entry, lod) === (entry.url ?? null)) return atLod ?? [];
    await this.templateBase(id);
    return this.clipCache.get(`${id}@base`) ?? [];
  }

  /**
   * The clips for whichever artifact `instantiatePreferred(id, preferBase)` placed:
   * the base artifact's clips when full detail is preferred and the base carries
   * any, otherwise the LOD0 resolution (`animationClips`, which itself falls back
   * to the base). Keeps a full-detail creature from fetching LOD0 just to read clips.
   */
  async animationClipsPreferred(
    id: string,
    preferBase: boolean,
  ): Promise<readonly AnimationClip[]> {
    if (preferBase && (await this.templateBase(id)) !== null) {
      const base = this.clipCache.get(`${id}@base`);
      if (base !== undefined && base.length > 0) return base;
    }
    return this.animationClips(id, 0);
  }

  /**
   * Drop all cached templates (e.g. on a zone/pack change) and free their GPU
   * resources — the cache owns them; clones only borrow (gpuOwnership.ts, H-1349).
   * Callers clear on teardown, after detaching every clone; a load still in flight
   * is freed when it lands.
   */
  clear(): void {
    for (const pending of this.cache.values()) {
      void pending.then(
        (template) => {
          if (template === null) return;
          stripEmitters(template); // the flames' resources are the flame system's
          releaseShared(template);
        },
        () => {},
      );
    }
    this.cache.clear();
    this.clipCache.clear();
  }

  private async loadTemplate(id: string, lod: number): Promise<Object3D | null> {
    const entry: AssetEntry | undefined = this.manifest.resolve(id);
    if (entry === undefined) return null; // unbound manifest or unknown id ⇒ primitive
    const handle = lodHandle(entry, lod); // consume AssetEntry.lods[] (near→far), fall back to url
    if (handle === null) return null; // no handle for this LOD ⇒ primitive
    const url = deliveryUrl(handle, entry.hash, this.httpBase);
    try {
      const gltf = await this.load(url);
      // VQ-001 (AD-194): decorate the shared template ONCE — shadow flags on every
      // mesh + the entry's optional `pbrHints` overrides. Clones inherit both
      // (clone(true) copies flags; materials are shared). Duck-typed + guarded, so
      // the tests' plain-object stubs pass through untouched (see assets/pbr.ts).
      decorateLoadedAsset(gltf.scene, entry);
      // q-npc-props-adoption-and-flame: hide the authored flame BEFORE the template's
      // resources are registered, attach the runtime flames AFTER (they are not the template's).
      const band = emitterBand(entry, handle);
      const hidden = this.hideForEmitters(id, gltf.scene, band);
      markShared(gltf.scene); // H-1349: clones borrow these; only clear() frees them
      this.attachForEmitters(id, gltf.scene, band, hidden);
      // AP5.S1: keep the artifact's clips alongside its template (see clipCache).
      this.clipCache.set(lod === 0 ? id : `${id}@${lod}`, gltf.animations ?? []);
      return gltf.scene;
    } catch (error) {
      this.onWarn(`asset '${id}' (lod ${lod}) failed to load (${url}); using placeholder`, error);
      // Evict the rejected attempt so a later request can retry rather than
      // permanently caching the failure.
      this.cache.delete(lod === 0 ? id : `${id}@${lod}`);
      return null;
    }
  }

  /** Hide an asset's authored flame for its declared emitters (only while flames are on). */
  private hideForEmitters(
    id: string,
    template: Object3D,
    band: string,
  ): Pick<EmitterDecoration, 'hiddenMeshes' | 'hiddenTriangles'> | null {
    const emitters = this.manifest.emittersFor(id);
    if (emitters === undefined || emitterFactory() === null) return null;
    return applyEmitterHides(template, emitters, band);
  }

  /** Attach an asset's runtime emitters and record what decoration did on the template. */
  private attachForEmitters(
    id: string,
    template: Object3D,
    band: string,
    hidden: Pick<EmitterDecoration, 'hiddenMeshes' | 'hiddenTriangles'> | null,
  ): void {
    if (hidden === null) return;
    const flames = attachEmitters(template, this.manifest.emittersFor(id));
    const decoration: EmitterDecoration = { band, flames, ...hidden };
    if (template.userData !== undefined) template.userData['gcEmitters'] = decoration;
  }

  private async loadTemplateBase(id: string): Promise<Object3D | null> {
    const entry: AssetEntry | undefined = this.manifest.resolve(id);
    if (entry === undefined) return null; // unbound manifest or unknown id ⇒ primitive
    const handle = entry.url ?? null; // the full-res textured render, skipping lods[]
    if (handle === null) return null; // no base url ⇒ primitive
    const url = deliveryUrl(handle, entry.hash, this.httpBase);
    try {
      const gltf = await this.load(url);
      // VQ-001 (AD-194): same decoration as the LOD path (shadow flags + pbrHints).
      decorateLoadedAsset(gltf.scene, entry);
      const hidden = this.hideForEmitters(id, gltf.scene, 'base'); // as the LOD path
      markShared(gltf.scene); // H-1349: as the LOD path
      this.attachForEmitters(id, gltf.scene, 'base', hidden);
      // AP5.S1: the base artifact is where the rig's clips actually live.
      this.clipCache.set(`${id}@base`, gltf.animations ?? []);
      return gltf.scene;
    } catch (error) {
      this.onWarn(`asset '${id}' (base) failed to load (${url}); using placeholder`, error);
      this.cache.delete(`${id}@base`);
      return null;
    }
  }
}
